import React, { useState, useEffect, useMemo } from 'react';
import {
  FiFolder,
  FiFile,
  FiFolderPlus,
  FiUpload,
  FiSearch,
  FiTrash2,
  FiArrowLeft,
  FiHome,
  FiDownload,
  FiEye,
  FiEdit2,
  FiAlertTriangle
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import { Button, Input, Modal } from '../../components';
import './style.css';

export default function EntityFilesModal({ isOpen, onClose, entity, entityType, company, category = 'geral' }) {
  // entityType: 'station', 'vehicle', 'customer', 'supplier', 'accountant', 'employee'

  // Data State
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals State
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  
  const [itemToDelete, setItemToDelete] = useState(null);
  const [renameItem, setRenameItem] = useState(null);
  const [renameName, setRenameName] = useState('');

  // Form State
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadFileName, setUploadFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragTargetId, setDragTargetId] = useState(null);

  const getEntityParams = () => {
    const params = {};
    if (!entity) return params;
    
    if (entityType === 'station') params.service_station_id = entity.id;
    if (entityType === 'vehicle') params.vehicle_id = entity.id;
    if (entityType === 'customer') params.customer_id = entity.id;
    if (entityType === 'supplier') params.supplier_id = entity.id;
    if (entityType === 'accountant') params.accountant_id = entity.id;
    if (entityType === 'employee') params.employee_id = entity.id;
    return params;
  };

  const getApiOptions = () => {
      const options = {};
      if (!entity) return options;

      if (entityType === 'vehicle') options.vehicleId = entity.id;
      if (entityType === 'customer') options.customerId = entity.id;
      if (entityType === 'supplier') options.supplierId = entity.id;
      if (entityType === 'accountant') options.accountantId = entity.id;
      return options;
  };

  const getEntityName = () => {
      if (!entity) return '';
      return entity.name || entity.plate || entity.corporate_name || entity.trade_name || 'Sem Nome';
  };

  // Load Content
  useEffect(() => {
    if (isOpen && entity && company) {
      loadContent();
    } else {
        // Reset state when closed
        setBreadcrumbs([]);
        setCurrentFolder(null);
        setFolders([]);
        setFiles([]);
        setSearchTerm('');
    }
  }, [isOpen, entity, company, currentFolder, category]);

  async function loadContent() {
    if (!company || !entity) return;
    setLoading(true);
    try {
      const folderId = currentFolder ? currentFolder.id : null;
      
      const serviceStationId = entityType === 'station' ? entity.id : null;
      const employeeId = entityType === 'employee' ? entity.id : null;
      const apiOptions = getApiOptions();

      // Pass params to ensure data isolation
      const foldersData = await Api.getFolders(company.id, folderId, serviceStationId, employeeId, category, apiOptions);
      const filesData = await Api.getFiles(folderId, company.id, serviceStationId, employeeId, category, apiOptions);
      
      console.log(`[EntityFilesModal] Loaded content for category '${category}'`, { 
        folders: foldersData.length, 
        files: filesData.length,
        params: { companyId: company.id, folderId, serviceStationId, employeeId, category, apiOptions }
      });

      // Client-side filtering safety net (optional, depends on API trust)
      // The API should handle filtering based on params
      
      const filteredFolders = Array.isArray(foldersData) ? foldersData : [];
      const filteredFiles = Array.isArray(filesData) ? filesData : [];

      setFolders(filteredFolders);
      setFiles(filteredFiles);
    } catch (error) {
      toast.error('Erro ao carregar arquivos.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  // Navigation Handlers
  function handleNavigate(folder) {
    setBreadcrumbs([...breadcrumbs, folder]);
    setCurrentFolder(folder);
    setSearchTerm('');
  }

  function handleNavigateUp() {
    if (breadcrumbs.length === 0) return;
    const newBreadcrumbs = [...breadcrumbs];
    newBreadcrumbs.pop();
    setBreadcrumbs(newBreadcrumbs);
    setCurrentFolder(newBreadcrumbs.length > 0 ? newBreadcrumbs[newBreadcrumbs.length - 1] : null);
    setSearchTerm('');
  }

  function handleNavigateBreadcrumb(index) {
    if (index === -1) {
      setBreadcrumbs([]);
      setCurrentFolder(null);
    } else {
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
      setBreadcrumbs(newBreadcrumbs);
      setCurrentFolder(newBreadcrumbs[newBreadcrumbs.length - 1]);
    }
    setSearchTerm('');
  }

  // Action Handlers
  async function handleCreateFolder() {
    if (!newFolderName.trim()) {
      toast.warning('Digite o nome da pasta.');
      return;
    }

    setIsSubmitting(true);
    try {
      const entityParams = getEntityParams();
      await Api.createFolder({
        name: newFolderName,
        company_id: company.id,
        ...entityParams,
        category: category,
        parent_id: currentFolder ? currentFolder.id : null,
        is_public: false
      });
      toast.success('Pasta criada com sucesso!');
      setIsFolderModalOpen(false);
      setNewFolderName('');
      loadContent();
    } catch (error) {
      toast.error('Erro ao criar pasta.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleUploadFile() {
    if (uploadFiles.length === 0) {
      toast.warning('Selecione pelo menos um arquivo.');
      return;
    }
    
    // Validate name only if single file
    if (uploadFiles.length === 1 && !uploadFileName.trim()) {
      toast.warning('Digite o nome do arquivo.');
      return;
    }

    setIsSubmitting(true);
    try {
      const entityParams = getEntityParams();
      
      const uploadPromises = Array.from(uploadFiles).map(file => {
          return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = async () => {
                  try {
                    const base64 = reader.result;
                    // Use custom name if single file, otherwise original name
                    const nameToUse = uploadFiles.length === 1 ? uploadFileName : file.name;
                    
                    await Api.createFile({
                      name: nameToUse,
                      folder_id: currentFolder ? currentFolder.id : null,
                      company_id: company.id,
                      ...entityParams,
                      category: category,
                      file_data: base64,
                      file_type: file.type,
                      file_size: file.size
                    });
                    resolve();
                  } catch (err) {
                    reject(err);
                  }
              };
              reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
          });
      });

      await Promise.all(uploadPromises);

      toast.success(uploadFiles.length > 1 ? 'Arquivos enviados com sucesso!' : 'Arquivo enviado com sucesso!');
      setIsUploadModalOpen(false);
      setUploadFiles([]);
      setUploadFileName('');
      loadContent();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao enviar arquivos.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function confirmDelete(item, type) {
    setItemToDelete({ ...item, type });
    setIsDeleteModalOpen(true);
  }

  async function handleDelete() {
    if (!itemToDelete) return;
    setIsSubmitting(true);
    try {
      if (itemToDelete.type === 'folder') {
        await Api.deleteFolder(itemToDelete.id);
      } else {
        await Api.deleteFile(itemToDelete.id);
      }
      toast.success('Item excluído com sucesso!');
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      loadContent();
    } catch (error) {
      toast.error('Erro ao excluir item.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function openRenameModal(item) {
    setRenameItem(item);
    setRenameName(item.name);
    setIsRenameModalOpen(true);
  }

  async function handleRename() {
    if (!renameName.trim()) {
      toast.warning('Digite o novo nome.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (renameItem.type === 'folder') {
        await Api.updateFolder(renameItem.id, { name: renameName });
      } else {
        await Api.updateFile(renameItem.id, { name: renameName });
      }
      toast.success('Renomeado com sucesso!');
      setIsRenameModalOpen(false);
      setRenameItem(null);
      setRenameName('');
      loadContent();
    } catch (error) {
      toast.error('Erro ao renomear.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDownload(file) {
    toast.info('Iniciando download...');
    try {
      const fullFile = await Api.getFile(file.id);
      if (fullFile && fullFile.file_data) {
        // WebView App Handling
      if (window.ReactNativeWebView) {
        let dataUrl = fullFile.file_data;
        // Ensure we have a valid data URL
        if (!dataUrl.startsWith('data:')) {
           const mime = fullFile.file_type || 'application/octet-stream';
           dataUrl = `data:${mime};base64,${fullFile.file_data}`;
        }

        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'download',
          name: fullFile.name,
          dataUrl: dataUrl
        }));
        return;
      }

        const link = document.createElement('a');
        link.href = fullFile.file_data;
        link.download = fullFile.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error('Dados do arquivo não encontrados.');
      }
    } catch (error) {
      toast.error('Erro ao baixar arquivo.');
    }
  }

  async function handlePreview(file) {
    try {
      const fullFile = await Api.getFile(file.id);
      if (fullFile && fullFile.file_data) {
        let base64 = fullFile.file_data;
        let contentType = fullFile.file_type || 'application/octet-stream';

        if (fullFile.file_data.includes(',')) {
            const parts = fullFile.file_data.split(',');
            base64 = parts[1];
        }

        // WebView App Handling
      if (window.ReactNativeWebView) {
        let dataUrl = fullFile.file_data;
        // Ensure we have a valid data URL
        if (!dataUrl.startsWith('data:')) {
           dataUrl = `data:${contentType};base64,${fullFile.file_data}`;
        }
        
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'file',
          name: fullFile.name,
          dataUrl: dataUrl
        }));
        return;
      }

        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });
        const url = URL.createObjectURL(blob);
        
        window.open(url, '_blank');
      } else {
        toast.error('Dados do arquivo não encontrados.');
      }
    } catch (error) {
      toast.error('Erro ao visualizar arquivo.');
    }
  }

  // Drag and Drop Handlers
  function handleDragStart(e, item) {
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: item.type, id: item.id }));
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e, targetId = null) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (targetId) {
      setDragTargetId(targetId);
    }
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setDragTargetId(null);
  }

  async function handleDrop(e, targetFolder) {
    e.preventDefault();
    setDragTargetId(null);
    const data = e.dataTransfer.getData('text/plain');
    if (!data) return;

    try {
      const draggedItem = JSON.parse(data);
      
      // Prevent dropping into self
      if (draggedItem.type === 'folder' && draggedItem.id === targetFolder.id) return;
      
      // Perform move
      if (draggedItem.type === 'folder') {
        await Api.updateFolder(draggedItem.id, { parent_id: targetFolder.id });
      } else {
        await Api.updateFile(draggedItem.id, { folder_id: targetFolder.id });
      }
      
      toast.success('Item movido com sucesso!');
      loadContent();
    } catch (error) {
      console.error('Drop error:', error);
      toast.error('Erro ao mover item.');
    }
  }

  async function handleDropOnBreadcrumb(e, targetFolder) {
    e.preventDefault();
    setDragTargetId(null);
    const data = e.dataTransfer.getData('text/plain');
    if (!data) return;

    try {
      const draggedItem = JSON.parse(data);
      const targetId = targetFolder ? targetFolder.id : null;

      // Prevent redundant moves
      if (draggedItem.type === 'folder' && draggedItem.id === targetId) return;

      if (draggedItem.type === 'folder') {
        await Api.updateFolder(draggedItem.id, { parent_id: targetId });
      } else {
        await Api.updateFile(draggedItem.id, { folder_id: targetId });
      }

      toast.success('Item movido com sucesso!');
      loadContent();
    } catch (error) {
      console.error('Drop error:', error);
      toast.error('Erro ao mover item.');
    }
  }

  // Filtering
  const filteredItems = useMemo(() => {
    let items = [
      ...folders.map(f => ({ ...f, type: 'folder' })),
      ...files.map(f => ({ ...f, type: 'file' }))
    ];
    
    if (searchTerm) {
      items = items.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by type (folder first) then name
    items.sort((a, b) => {
        if (a.type === b.type) {
            return a.name.localeCompare(b.name);
        }
        return a.type === 'folder' ? -1 : 1;
    });

    return items;
  }, [folders, files, searchTerm]);

  const isMobile = window.innerWidth <= 768;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${category === 'ponto' ? 'Arquivos de Ponto' : 'Arquivos'} - ${company ? (company.corporate_name || company.trade_name || company.name || company.razao_social || company.nome_fantasia || 'Sem Nome') : ''} - ${getEntityName()}`}
      size="xl"
      closeOnOverlayClick={false}
    >
      <div className="efm-container">
        {/* Header Actions */}
        <div className="efm-header-actions">
           <div className="efm-search">
              <Input
                placeholder="Buscar arquivos..."
                icon={FiSearch}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="efm-search-input"
              />
           </div>
           <div className="efm-buttons">
              <Button
                onClick={() => {
                  setNewFolderName('');
                  setIsFolderModalOpen(true);
                }}
                startIcon={<FiFolderPlus />}
                size="sm"
              >
                {isMobile ? '' : 'Pasta'}
              </Button>
              <Button
                onClick={() => {
                  setUploadFiles([]);
                  setUploadFileName('');
                  setIsUploadModalOpen(true);
                }}
                variant="outline"
                startIcon={<FiUpload />}
                size="sm"
              >
                {isMobile ? '' : 'Arquivos'}
              </Button>
           </div>
        </div>

        {/* Breadcrumbs */}
        <div className="efm-breadcrumbs" onDragOver={e => e.preventDefault()}>
          <div 
            className={`efm-breadcrumb-item ${!currentFolder ? 'active' : ''}`}
            onClick={() => handleNavigateBreadcrumb(-1)}
            onDrop={(e) => handleDropOnBreadcrumb(e, null)}
            onDragOver={(e) => handleDragOver(e, 'root')}
            onDragLeave={handleDragLeave}
            style={dragTargetId === 'root' ? { backgroundColor: 'var(--primary-light)', color: 'var(--primary-color)' } : {}}
          >
            <FiHome size={14} />
            <span>Início</span>
          </div>
          {breadcrumbs.map((folder, index) => (
            <React.Fragment key={folder.id}>
              <span className="efm-breadcrumb-separator">/</span>
              <div 
                className={`efm-breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
                onClick={() => handleNavigateBreadcrumb(index)}
                onDrop={(e) => handleDropOnBreadcrumb(e, folder)}
                onDragOver={(e) => handleDragOver(e, `crumb-${folder.id}`)}
                onDragLeave={handleDragLeave}
                style={dragTargetId === `crumb-${folder.id}` ? { backgroundColor: 'var(--primary-light)', color: 'var(--primary-color)' } : {}}
              >
                <FiFolder size={14} />
                <span>{folder.name}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Content Grid */}
        <div className={`efm-grid ${(loading || filteredItems.length === 0) ? 'efm-grid-flex' : ''}`}>
          {currentFolder && (
            <div className="efm-grid-item efm-back-folder" onClick={handleNavigateUp}>
               <div className="efm-icon-wrapper">
                 <FiArrowLeft size={24} />
               </div>
               <span className="efm-item-name">Voltar</span>
            </div>
          )}

          {loading ? (
             <div className="efm-loading">Carregando...</div>
          ) : filteredItems.length === 0 ? (
             <div className="efm-empty">
                <FiFolder size={48} style={{ opacity: 0.3 }} />
                <span>Nenhum item encontrado</span>
             </div>
          ) : (
            filteredItems.map(item => (
              <div 
                key={`${item.type}-${item.id}`}
                className={`efm-grid-item ${dragTargetId === item.id ? 'drag-over' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, item)}
                onDragOver={(e) => item.type === 'folder' ? handleDragOver(e, item.id) : null}
                onDragLeave={handleDragLeave}
                onDrop={(e) => item.type === 'folder' ? handleDrop(e, item) : null}
                onClick={() => item.type === 'folder' ? handleNavigate(item) : handlePreview(item)}
              >
                <div className={`efm-icon-wrapper ${item.type}`}>
                  {item.type === 'folder' ? <FiFolder size={32} /> : <FiFile size={32} />}
                </div>
                <div className="efm-item-info">
                  <span className="efm-item-name" title={item.name}>{item.name}</span>
                  {item.type === 'file' && (
                     <span className="efm-item-meta">{new Date(item.createdAt || item.created_at).toLocaleDateString('pt-BR')}</span>
                  )}
                </div>
                {item.type === 'file' && item.signature_required ? (
                  <div className={`efm-signature-badge ${item.is_signed ? 'signed' : 'not-signed'}`}>
                    {item.is_signed ? 'Assinado' : 'Não Assinado'}
                  </div>
                ) : null}
                <div className="efm-item-actions" onClick={e => e.stopPropagation()}>
                    <button className="efm-action-btn" onClick={() => openRenameModal(item)} title="Renomear">
                        <FiEdit2 size={12} />
                    </button>
                    {item.type === 'file' && (
                        <button className="efm-action-btn" onClick={() => handleDownload(item)} title="Baixar">
                            <FiDownload size={12} />
                        </button>
                    )}
                    {item.type === 'file' && (
                        <button className="efm-action-btn" onClick={() => handlePreview(item)} title="Visualizar">
                            <FiEye size={12} />
                        </button>
                    )}
                    <button className="efm-action-btn danger" onClick={() => confirmDelete(item, item.type)} title="Excluir">
                        <FiTrash2 size={12} />
                    </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Sub-modals */}
      <Modal
        isOpen={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
        title="Nova Pasta"
        size="sm"
      >
        <div className="efm-modal-body">
           <Input
             label="Nome da Pasta"
             value={newFolderName}
             onChange={e => setNewFolderName(e.target.value)}
             placeholder="Digite o nome da pasta"
           />
        </div>
        <div className="delete-modal-actions">
           <Button onClick={() => setIsFolderModalOpen(false)} className="cancel-button">Cancelar</Button>
           <Button onClick={handleCreateFolder} disabled={isSubmitting}>Criar Pasta</Button>
        </div>
      </Modal>

      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title="Enviar Arquivos"
        size="sm"
      >
        <div className="efm-modal-body">
           <div className="efm-file-input-wrapper">
             <input 
               type="file" 
               id="file-upload"
               multiple
               onChange={e => {
                 if(e.target.files && e.target.files.length > 0) {
                    setUploadFiles(e.target.files);
                    if (e.target.files.length === 1) {
                        setUploadFileName(e.target.files[0].name);
                    } else {
                        setUploadFileName('');
                    }
                 }
               }} 
             />
             <label htmlFor="file-upload" className="efm-file-input-label">
                <FiUpload size={24} />
                <span>
                  {uploadFiles.length > 0 ? (
                      uploadFiles.length === 1 ? (
                          <span className="efm-file-selected">{uploadFiles[0].name}</span>
                      ) : (
                          <span className="efm-file-selected">{uploadFiles.length} arquivos selecionados</span>
                      )
                  ) : 'Clique para escolher arquivos'}
                </span>
             </label>
           </div>
           
           {uploadFiles.length === 1 && (
               <Input
                 label="Nome do Arquivo"
                 value={uploadFileName}
                 onChange={e => setUploadFileName(e.target.value)}
                 placeholder="Nome do arquivo..."
               />
           )}
           
           {uploadFiles.length > 1 && (
               <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                   Os arquivos serão salvos com seus nomes originais.
               </div>
           )}
        </div>

        <div className="delete-modal-actions">
           <Button onClick={() => setIsUploadModalOpen(false)} className="cancel-button">Cancelar</Button>
           <Button onClick={handleUploadFile} disabled={isSubmitting || uploadFiles.length === 0}>Enviar Arquivos</Button>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar Exclusão"
        size="sm"
      >
        <div className="delete-modal-content">
           <p>Tem certeza que deseja excluir <strong>"{itemToDelete?.name}"</strong>?</p>
           <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--danger-color)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FiAlertTriangle /> Essa ação não poderá ser desfeita.
           </p>
        </div>
        <div className="delete-modal-actions">
           <Button onClick={() => setIsDeleteModalOpen(false)} className="cancel-button">Cancelar</Button>
           <Button onClick={handleDelete} type="button" disabled={isSubmitting}>Excluir</Button>
        </div>
      </Modal>

      <Modal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        title="Renomear Item"
        size="sm"
      >
        <div className="efm-modal-body">
           <Input
             label="Novo Nome"
             value={renameName}
             onChange={e => setRenameName(e.target.value)}
             placeholder="Novo nome do item"
           />
        </div>
        <div className="delete-modal-actions">
           <Button onClick={() => setIsRenameModalOpen(false)} className="cancel-button">Cancelar</Button>
           <Button onClick={handleRename} disabled={isSubmitting}>Renomear</Button>
        </div>
      </Modal>
    </Modal>
  );
}
